import { useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { getManagedEventsBy } from "@/features/supply-config/managed-events-api";
import { useProducts, ParticipantSlot } from "@/features/products/products-api";
import { FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import ImageUploadField from "@/features/products/ui/image-upload-field";
import { uploadBundleSessionImage } from "@/features/bundles/services/upload-session-image";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BundleWizardData } from "./wizard-form";

// Tokens offered alongside the product's participant slots.
const PARTICIPANT_TOKENS = [
  { value: "ANY", label: "Any participant" },
  { value: "ALL", label: "Everyone (joint)" },
] as const;

// participant → legacy booked_by (dual-write so any legacy reader stays consistent).
const bookedByFor = (participant: string): "PRIMARY_USER" | "ANY_USER" =>
  participant === "ANY" ? "ANY_USER" : "PRIMARY_USER";

type SessionRow = BundleWizardData["session_types"][number];

// priority is derived from list order — reindex on every mutation.
const reindex = (arr: SessionRow[]): SessionRow[] =>
  arr.map((r, i) => ({ ...r, priority: i + 1 }));

// One row per session: managed event + who books it. Order = priority; drag the
// handle to reorder. Stored as a JSON array (extensible). Total sessions = rows.
export default function SessionTypesField({
  form,
}: {
  form: UseFormReturn<BundleWizardData>;
}) {
  const productId = useWatch({ control: form.control, name: "product_id" });
  const regionId = useWatch({ control: form.control, name: "region_id" });
  const rows = useWatch({ control: form.control, name: "session_types" }) ?? [];
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const { data: managedEvents = [] } = useQuery({
    queryKey: ["managed-events-by", productId, regionId],
    queryFn: () =>
      getManagedEventsBy({
        product_id: productId ?? undefined,
        region_id: regionId ?? undefined,
      }),
    enabled: !!productId && !!regionId,
  });

  // The selected product's participant slots (from the cached products list).
  // Falls back to the Individual default so the picker always has a slot.
  const products = useProducts().data ?? [];
  const product = products.find((p) => p.id === productId);
  const slots: ParticipantSlot[] = product?.participant_config?.slots?.length
    ? product.participant_config.slots
    : [{ key: "self", label: "Client", min: 1, max: 1 }];

  // Display value for a row: explicit participant, else derived from legacy booked_by.
  const effectiveParticipant = (row: SessionRow) =>
    row.participant ??
    (row.booked_by === "ANY_USER" ? "ANY" : slots[0]?.key ?? "ANY");

  const setRows = (next: SessionRow[]) =>
    form.setValue("session_types", reindex(next), { shouldValidate: true });

  const addRow = () => {
    const participant = slots[0]?.key ?? "ANY";
    setRows([
      ...rows,
      {
        priority: rows.length + 1,
        managed_event_id: "",
        participant,
        booked_by: bookedByFor(participant),
      },
    ]);
  };
  const patchRow = (i: number, patch: Partial<SessionRow>) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));

  const moveRow = (from: number, to: number) => {
    if (from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
  };

  // Human label for a session row (booking-rules dropdowns reference other rows).
  const meName = (id: string) =>
    managedEvents.find((m) => m.id === id)?.name;
  const sessionLabel = (r: SessionRow, idx: number) =>
    (r.title && r.title.trim()) || meName(r.managed_event_id) || `Session ${idx + 1}`;

  // Prerequisite options for row `currentIndex`: the OTHER sessions, referenced
  // by their managed_event_id, DEDUPED by managed event (a prerequisite matches
  // any session of that event). Deduping avoids duplicate <SelectItem>s sharing
  // a value — which made Radix render the selected label N times. We only skip
  // the current row itself (not its managed event, which may be shared by other
  // sessions the user legitimately wants to reference).
  const prereqOptions = (currentIndex: number) => {
    const seen = new Set<string>();
    const out: { id: string; label: string }[] = [];
    rows.forEach((r, idx) => {
      if (idx === currentIndex || !r.managed_event_id) return;
      if (seen.has(r.managed_event_id)) return;
      seen.add(r.managed_event_id);
      out.push({ id: r.managed_event_id, label: sessionLabel(r, idx) });
    });
    return out;
  };

  const blocked = !productId || !regionId;

  return (
    <FormItem>
      <div className="flex items-center justify-between">
        <FormLabel>
          Sessions <span className="text-red-500">*</span>
          <span className="ml-1 text-[11px] font-normal text-gray-400">
            (drag to reorder — top = priority 1)
          </span>
        </FormLabel>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={blocked}
          onClick={addRow}
        >
          <Plus className="h-4 w-4 mr-1" /> Add session
        </Button>
      </div>

      {blocked ? (
        <p className="text-xs text-amber-600">
          Pick a product and region in Basics first.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">
          No sessions yet — click "Add session".
        </p>
      ) : (
        <TooltipProvider>
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div
              key={i}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIndex !== null) moveRow(dragIndex, i);
                setDragIndex(null);
              }}
              onDragEnd={() => setDragIndex(null)}
              className={`flex flex-col gap-3 rounded-md border border-gray-200 p-2 bg-white ${
                dragIndex === i ? "opacity-50" : ""
              }`}
            >
              <div className="grid grid-cols-[auto_1fr_150px_auto] items-end gap-2">
              <div className="flex items-center gap-1 pb-1.5">
                <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                <Badge variant="outline">{i + 1}</Badge>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-gray-500 mb-1">Managed event</p>
                {(() => {
                  const selected = managedEvents.find(
                    (m) => m.id === row.managed_event_id,
                  );
                  const selectedLabel = selected
                    ? `${selected.name}${selected.duration_minutes ? ` · ${selected.duration_minutes}m` : ""}`
                    : "";
                  return (
                    <Select
                      value={row.managed_event_id}
                      onValueChange={(v) => patchRow(i, { managed_event_id: v })}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectTrigger className="h-8 text-sm min-w-0">
                            <SelectValue placeholder="Select managed event" />
                          </SelectTrigger>
                        </TooltipTrigger>
                        {selectedLabel && (
                          <TooltipContent className="max-w-xs break-words">
                            {selectedLabel}
                          </TooltipContent>
                        )}
                      </Tooltip>
                      <SelectContent>
                        {managedEvents.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}
                            {m.duration_minutes ? ` · ${m.duration_minutes}m` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div>
                <p className="text-[11px] text-gray-500 mb-1">Participant</p>
                <Select
                  value={effectiveParticipant(row)}
                  onValueChange={(v) =>
                    patchRow(i, { participant: v, booked_by: bookedByFor(v) })
                  }
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Participant" />
                  </SelectTrigger>
                  <SelectContent>
                    {slots.map((s) => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.label}
                      </SelectItem>
                    ))}
                    {PARTICIPANT_TOKENS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeRow(i)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
              </div>

              {/* Optional per-session step presentation — overrides the
                  fallback step card (badge + participant label) on the app. */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_128px] gap-3 border-t border-gray-100 pt-2">
                <div className="space-y-2 min-w-0">
                  <div>
                    <p className="text-[11px] text-gray-500 mb-1">
                      Step title{" "}
                      <span className="text-gray-400">(optional)</span>
                    </p>
                    <Input
                      className="h-8 text-sm"
                      placeholder="e.g. Your private session"
                      value={row.title ?? ""}
                      onChange={(e) => patchRow(i, { title: e.target.value })}
                    />
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500 mb-1">
                      Step description{" "}
                      <span className="text-gray-400">(optional)</span>
                    </p>
                    <textarea
                      className="w-full min-h-[52px] rounded-md border border-gray-200 p-2 text-sm"
                      placeholder="Short description shown on the step card"
                      value={row.description ?? ""}
                      onChange={(e) =>
                        patchRow(i, { description: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500 mb-1">
                      Booking button label{" "}
                      <span className="text-gray-400">(optional)</span>
                    </p>
                    <Input
                      className="h-8 text-sm"
                      placeholder="Schedule next session"
                      value={row.book_button_label ?? ""}
                      onChange={(e) =>
                        patchRow(i, { book_button_label: e.target.value })
                      }
                    />
                    <p className="text-[10px] text-gray-400 mt-1">
                      Overrides the &quot;Schedule next session&quot; button when
                      this is the user&apos;s next session.
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-500 mb-1">
                      Completion page{" "}
                      <span className="text-gray-400">(assessment products)</span>
                    </p>
                    <Select
                      value={row.completion_page ?? "none"}
                      onValueChange={(v) =>
                        patchRow(i, {
                          completion_page:
                            v === "none"
                              ? undefined
                              : (v as "results" | "report"),
                        })
                      }
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="results">
                          Results (post-exploration summary)
                        </SelectItem>
                        <SelectItem value="report">
                          Report (this is the diagnostic)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Which page shows once the journey reaches this session.
                      Mark the diagnostic session &quot;Report&quot;.
                    </p>
                  </div>
                </div>
                <div>
                  <ImageUploadField
                    label="Step image"
                    value={row.image ?? ""}
                    onChange={(url) => patchRow(i, { image: url })}
                    uploader={uploadBundleSessionImage}
                  />
                </div>
              </div>

              {/* Optional per-session rules: booking prerequisite + expiry
                  extension. Config-driven flow (e.g. men's-health: follow-up
                  unlocks after consult attended; consult extends expiry 30d). */}
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Booking rules{" "}
                  <span className="font-normal normal-case">(optional)</span>
                </p>

                {/* Prerequisite: unlock only after another session's state. */}
                <div className="rounded-md border border-gray-200 p-3 space-y-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-[180px_minmax(0,1fr)] sm:items-center gap-x-3 gap-y-1">
                    <span className="text-xs font-medium text-gray-700">
                      Unlock this session
                    </span>
                    <Select
                      value={
                        row.prerequisite?.after_managed_event_id ?? "__none__"
                      }
                      onValueChange={(v) =>
                        patchRow(i, {
                          prerequisite:
                            v === "__none__"
                              ? undefined
                              : {
                                  after_managed_event_id: v,
                                  condition:
                                    row.prerequisite?.condition ?? "ATTENDED",
                                  ...(row.prerequisite?.blocked_reason
                                    ? {
                                        blocked_reason:
                                          row.prerequisite.blocked_reason,
                                      }
                                    : {}),
                                },
                        })
                      }
                    >
                      <SelectTrigger className="h-8 text-sm w-full min-w-0 [&>span]:truncate">
                        <SelectValue placeholder="Anytime (no prerequisite)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          Anytime (no prerequisite)
                        </SelectItem>
                        {prereqOptions(i).map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            After: {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {row.prerequisite ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] sm:items-center gap-x-3 gap-y-1">
                        <span className="text-xs text-gray-500">
                          …once that session is
                        </span>
                        <Select
                          value={row.prerequisite.condition}
                          onValueChange={(v) =>
                            patchRow(i, {
                              prerequisite: {
                                ...row.prerequisite!,
                                condition: v as "BOOKED" | "ATTENDED",
                              },
                            })
                          }
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BOOKED">Booked</SelectItem>
                            <SelectItem value="ATTENDED">
                              Booked &amp; attended
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] sm:items-center gap-x-3 gap-y-1">
                        <span className="text-xs text-gray-500">
                          Blocked message
                        </span>
                        <Input
                          className="h-8 text-sm"
                          placeholder="Optional — shown while locked"
                          value={row.prerequisite.blocked_reason ?? ""}
                          onChange={(e) =>
                            patchRow(i, {
                              prerequisite: {
                                ...row.prerequisite!,
                                blocked_reason: e.target.value || undefined,
                              },
                            })
                          }
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] sm:items-center gap-x-3 gap-y-1">
                        <span className="text-xs text-gray-500">
                          …and require metadata key
                        </span>
                        <div>
                          <Input
                            className="h-8 text-sm"
                            placeholder="Optional — e.g. diva_report_key"
                            value={row.prerequisite.requires_metadata_key ?? ""}
                            onChange={(e) =>
                              patchRow(i, {
                                prerequisite: {
                                  ...row.prerequisite!,
                                  requires_metadata_key:
                                    e.target.value.trim() || undefined,
                                },
                              })
                            }
                          />
                          <p className="text-[10px] text-gray-400 mt-1">
                            Stays locked until this key exists on the usage
                            metadata (e.g. <code>diva_report_key</code> =
                            report PDF uploaded by the care team).
                          </p>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>

                {/* Expiry extension: this session extends the bundle's expiry. */}
                <div className="rounded-md border border-gray-200 p-3 space-y-2.5">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-primary"
                      checked={!!row.expiry_extension}
                      onChange={(e) =>
                        patchRow(i, {
                          expiry_extension: e.target.checked
                            ? {
                                days: row.expiry_extension?.days ?? 30,
                                condition:
                                  row.expiry_extension?.condition ?? "ATTENDED",
                                anchor:
                                  row.expiry_extension?.anchor ?? "SESSION_END",
                              }
                            : undefined,
                        })
                      }
                    />
                    Extend bundle expiry from this session
                  </label>
                  {row.expiry_extension ? (
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <p className="text-[11px] text-gray-500 mb-1">Days</p>
                        <Input
                          type="number"
                          min={1}
                          className="h-8 w-24 text-sm"
                          value={row.expiry_extension.days}
                          onChange={(e) =>
                            patchRow(i, {
                              expiry_extension: {
                                ...row.expiry_extension!,
                                days: Math.max(1, Number(e.target.value) || 1),
                              },
                            })
                          }
                        />
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-500 mb-1">
                          Measured from
                        </p>
                        <Select
                          value={row.expiry_extension.anchor ?? "SESSION_END"}
                          onValueChange={(v) =>
                            patchRow(i, {
                              expiry_extension: {
                                ...row.expiry_extension!,
                                anchor: v as "SESSION_END" | "SESSION_START",
                              },
                            })
                          }
                        >
                          <SelectTrigger className="h-8 w-[150px] text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SESSION_END">
                              Session end
                            </SelectItem>
                            <SelectItem value="SESSION_START">
                              Session start
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-500 mb-1">Applied when</p>
                        <Select
                          value={row.expiry_extension.condition}
                          onValueChange={(v) =>
                            patchRow(i, {
                              expiry_extension: {
                                ...row.expiry_extension!,
                                condition: v as "BOOKED" | "ATTENDED",
                              },
                            })
                          }
                        >
                          <SelectTrigger className="h-8 w-[140px] text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BOOKED">Booked</SelectItem>
                            <SelectItem value="ATTENDED">Attended</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500">
            Total sessions: <strong>{rows.length}</strong>
          </p>
        </div>
        </TooltipProvider>
      )}
      <FormMessage />
    </FormItem>
  );
}
